import { FC } from "react";
import {
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useAppState } from "../../state/App";
import { useConversationFavorite } from "../../hooks/useConversationFavorite";
import { ConversationSearchResult } from "../../types";
import { Timestamp } from "../common/Timestamp";

/** Max characters for the matching message snippet, tuned for ~2 caption lines in the side panel. */
export const SEARCH_SNIPPET_MAX_LENGTH = 150;

type ChatHistorySearchResultsProps = {
  results: ConversationSearchResult[];
  isLoading: boolean;
  query: string;
};

const HighlightMatch: FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  if (!query) return <>{text}</>;
  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} style={{ background: "#ffe082", borderRadius: 2 }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

type SearchResultItemProps = {
  result: ConversationSearchResult;
  isSelected: boolean;
  isGenerating: boolean;
  query: string;
  onSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
};

const SearchResultItem: FC<SearchResultItemProps> = ({
  result,
  isSelected,
  isGenerating,
  query,
  onSelect,
  onToggleFavorite,
}) => {
  const theme = useTheme();

  return (
    <div
      key={result._id}
      role="button"
      tabIndex={0}
      aria-label="search result item"
      className="px-4 py-2"
      onClick={() => onSelect(result._id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(result._id);
        }
      }}
      style={{
        backgroundColor: isSelected
          ? theme.palette.action.hover
          : "transparent",
        cursor: isGenerating ? "not-allowed" : "pointer",
      }}
    >
      <div className="flex justify-between items-center">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap>
            <HighlightMatch text={result.title} query={query} />
          </Typography>
          {result.updated && (
            <Timestamp date={result.updated} noWrap sx={{ lineHeight: 1 }} />
          )}
        </div>
        <IconButton
          size="small"
          title={
            result.is_favorite ? "Remove from favorites" : "Add to favorites"
          }
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(result._id);
          }}
        >
          {result.is_favorite ? (
            <StarIcon fontSize="small" color="warning" />
          ) : (
            <StarBorderIcon fontSize="small" />
          )}
        </IconButton>
      </div>
      {result.matching_message && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          <HighlightMatch text={result.matching_message} query={query} />
        </Typography>
      )}
    </div>
  );
};

const ChatHistorySearchResults: FC<ChatHistorySearchResultsProps> = ({
  results,
  isLoading,
  query,
}) => {
  const {
    state: { currentConversationId, isGenerating },
    actions: { setCurrentConversationId, setCurrentTTSUrl, setIsPlaying },
  } = useAppState();
  const { mutateAsync: toggleFavorite } = useConversationFavorite();

  const handleSelect = (id: string) => {
    if (isGenerating) return;
    setCurrentTTSUrl(undefined);
    setIsPlaying(false);
    setCurrentConversationId(id);
  };

  if (isLoading) {
    return (
      <div className="mt-4 flex justify-center">
        <div className="mr-2 flex items-center">
          <CircularProgress size={16} />
        </div>
        <Typography variant="caption">Searching...</Typography>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="mt-4 flex justify-center">
        <Typography
          style={{ alignSelf: "center", fontWeight: "400", fontSize: 14 }}
        >
          No results found.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="caption" className="px-4" color="textSecondary">
        {results.length} result{results.length !== 1 ? "s" : ""}
      </Typography>
      {results.map((result) => (
        <SearchResultItem
          key={result._id}
          result={result}
          isSelected={result._id === currentConversationId}
          isGenerating={isGenerating}
          query={query}
          onSelect={handleSelect}
          onToggleFavorite={toggleFavorite}
        />
      ))}
    </div>
  );
};

export default ChatHistorySearchResults;
